import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for scroll-triggered reveal animations
 * Uses IntersectionObserver to detect when elements enter the viewport
 * and adds 'revealed' class to trigger CSS transitions
 */
export const useScrollReveal = (options = {}) => {
  const observerRef = useRef(null);

  const defaultOptions = {
    threshold: 0.1,      // Trigger when 10% of element is visible
    rootMargin: '0px 0px -50px 0px', // Trigger slightly before element reaches viewport bottom
    once: true,          // Only reveal once (don't hide when scrolling back up)
    ...options
  };

  const setupObserver = useCallback(() => {
    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');

          // If once is true, stop observing after reveal
          if (defaultOptions.once) {
            observerRef.current?.unobserve(entry.target);
          }
        } else if (!defaultOptions.once) {
          // If once is false, remove revealed class when out of view
          entry.target.classList.remove('revealed');
        }
      });
    }, {
      threshold: defaultOptions.threshold,
      rootMargin: defaultOptions.rootMargin
    });

    // Find and observe all scroll-reveal elements
    const revealClasses = [
      'scroll-reveal',
      'scroll-reveal-up',
      'scroll-reveal-down',
      'scroll-reveal-left',
      'scroll-reveal-right',
      'scroll-reveal-scale',
      'scroll-reveal-rotate',
      'scroll-reveal-blur',
      'section-header-reveal',
      'card-reveal',
      'image-reveal',
      'text-reveal',
      'list-item-reveal'
    ];

    revealClasses.forEach(className => {
      const elements = document.querySelectorAll(`.${className}:not(.revealed)`);
      elements.forEach(el => {
        observerRef.current?.observe(el);
      });
    });
  }, [defaultOptions.once, defaultOptions.threshold, defaultOptions.rootMargin]);

  useEffect(() => {
    // Initial setup
    setupObserver();

    // Re-run observer setup when DOM changes (for dynamic content)
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          shouldUpdate = true;
        }
      });
      if (shouldUpdate) {
        // Debounce the update
        setTimeout(setupObserver, 100);
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup
    return () => {
      observerRef.current?.disconnect();
      mutationObserver.disconnect();
    };
  }, [setupObserver]);

  // Return a function to manually trigger re-observation
  return { refresh: setupObserver };
};

export default useScrollReveal;
